
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { MessageCircle, X } from 'lucide-react';
import { ChatbotInterface } from './ChatbotInterface';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface FloatingChatbotProps {
  grade?: string;
  subject?: string;
}

export const FloatingChatbot: React.FC<FloatingChatbotProps> = ({
  grade: propGrade = '1',
  subject: propSubject
}) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [userGrade, setUserGrade] = useState(propGrade);
  const [isLoading, setIsLoading] = useState(true);

  // Extract subject from URL if not provided as prop
  const location = window.location;
  const currentSubject = propSubject || (() => {
    const pathParts = location.pathname.split('/');
    const subjectIndex = pathParts.findIndex(part => part === 'subject');
    return subjectIndex !== -1 && pathParts[subjectIndex + 1] ? pathParts[subjectIndex + 1] : undefined;
  })();

  useEffect(() => {
    if (user) {
      loadUserGradePreference();
    } else {
      setIsLoading(false);
    }
  }, [user]);

  const loadUserGradePreference = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      // First try to get from user learning preferences
      const { data: preferences, error: prefError } = await supabase
        .from('user_learning_preferences')
        .select('grade')
        .eq('user_id', user.id)
        .order('last_updated', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (preferences && preferences.grade) {
        setUserGrade(preferences.grade);
        return;
      }

      // If no preferences, try to get from recent chat sessions
      const { data: sessions, error: sessionsError } = await supabase
        .from('chat_sessions')
        .select('grade')
        .eq('user_id', user.id)
        .order('last_activity', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (sessions && sessions.grade) {
        setUserGrade(sessions.grade);
        return;
      }

      // If no data found, try to get from recent test sessions
      const { data: testSessions, error: testError } = await supabase
        .from('test_sessions')
        .select('grade')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (testSessions && testSessions.grade) {
        setUserGrade(testSessions.grade);
      }

    } catch (error) {
      console.error('Error loading user grade preference:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveUserGradePreference = async (grade: string) => {
    if (!user) return;

    try {
      // Update or insert user learning preferences
      const { error } = await supabase
        .from('user_learning_preferences')
        .upsert({
          user_id: user.id,
          grade: grade,
          last_updated: new Date().toISOString()
        }, {
          onConflict: 'user_id,grade'
        });

      if (error && error.code !== '23505') { // Ignore unique constraint violations
        console.error('Error saving grade preference:', error);
      }
    } catch (error) {
      console.error('Error saving user grade preference:', error);
    }
  };

  const handleChatbotOpen = () => {
    setIsOpen(true);
    // Save the current grade preference when opening chatbot
    saveUserGradePreference(userGrade);
  };

  if (!user || isLoading) return null;

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <Button
          onClick={handleChatbotOpen}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg z-[9999] hover:scale-110 transition-transform bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
          size="sm"
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
      )}

      {/* Chat Interface */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-80 h-96 z-[9999] shadow-2xl">
          <ChatbotInterface
            grade={userGrade}
            subject={currentSubject}
            onClose={() => setIsOpen(false)}
          />
        </div>
      )}
    </>
  );
};
